import { ArrowUpRight, CheckCircle2, ChevronDown, ChevronRight, Download, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { useEditorStore } from "@/store/editorStore";
import type { Tables } from "@/types/database";
import { downloadFile, exportWebTour, publishWebTour } from "@/utils/exportProject";
import completionSoundUrl from "@/assets/audio/completion-sound.mp3";
import { confettiService } from "@/utils/confettiService";
import { api } from "../../../convex/_generated/api";

type Project = Tables<"projects">;
type Panorama = Tables<"panoramas">;
type Hotspot = Tables<"hotspots">;

interface ExportDialogProps {
  onClose: () => void;
  project: Project;
  panoramas: Panorama[];
  hotspots: Hotspot[];
}

export const ExportDialog = ({
  onClose,
  project,
  panoramas,
  hotspots,
}: ExportDialogProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const [publishedTourUrl, setPublishedTourUrl] = useState<string | null>(null);
  const [publishedProductionUrl, setPublishedProductionUrl] = useState<string | null>(null);

  const [provider, setProvider] = useState<"360crafter" | "custom-s3" | null>(null);
  const [copyTooltip, setCopyTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    text: string;
  }>({ visible: false, x: 0, y: 0, text: "Click to copy URL" });
  const [stagingChecked, setStagingChecked] = useState(true);
  const [productionChecked, setProductionChecked] = useState(true);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [domainActionId, setDomainActionId] = useState<string | null>(null);
  const [domainVerifyHints, setDomainVerifyHints] = useState<
    Record<string, { txt?: boolean | null; cname?: boolean | null }>
  >({});
  const [expandedDomainIds, setExpandedDomainIds] = useState<Set<string>>(new Set());
  const [hoveredDomainId, setHoveredDomainId] = useState<string | null>(null);
  const [publishTooltip, setPublishTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
  }>({ visible: false, x: 0, y: 0 });
  const [highlightTrash, setHighlightTrash] = useState(false);
  const checkedDomainIdsRef = useRef<Set<string>>(new Set());
  const { toast } = useToast();
  const { user } = useAuth();
  const projectBackend = useEditorStore((state) => state.projectBackend);
  const allowLocalExport = isElectron() && projectBackend === "local";
  const shouldUseConvex = !allowLocalExport;
  const craftspaceId = (project.craftspace_id as unknown as string) || "default";
  const craftspace = useQuery(
    api.craftspaces.get,
    shouldUseConvex ? { id: project.craftspace_id as unknown as any } : "skip",
  ) as { slug?: string } | null;
  const projectInternalId =
    typeof (project as { _id?: string })._id === "string"
      ? (project as { _id: string })._id
      : (project.id as unknown as string);
  const latestPublishedTour = useQuery(
    api.publishedTours.getLatestByProject,
    shouldUseConvex && projectInternalId
      ? { projectId: projectInternalId as unknown as any }
      : "skip",
  ) as { bucket_url?: string; tour_url?: string } | null | undefined;
  const customDomains = useQuery(
    api.customDomains.listByProject,
    shouldUseConvex ? { projectId: projectInternalId as unknown as any } : "skip",
  ) as
    | Array<{
        _id: string;
        domain: string;
        token: string;
        status?: "pending" | "verified" | "failed";
        is_selected?: boolean;
      }>
    | null;
  const createDomain = useMutation(api.customDomains.create);
  const removeDomain = useMutation(api.customDomains.remove);
  const setSelected = useMutation(api.customDomains.setSelected);
  const clearDomain = useMutation(api.customDomains.clearForProject);
  const verifyDomain = useAction(api.customDomains.verify);
  const ensureSsl = useAction(api.customDomains.ensureSslForHostname);
  const ensureEdgeRule = useAction(api.customDomains.ensureEdgeRuleForDomain);
  const basePublicUrl =
    (typeof import.meta.env.VITE_CONVEX_URL === "string" &&
      import.meta.env.VITE_CONVEX_URL.startsWith("http")
      ? new URL(import.meta.env.VITE_CONVEX_URL).origin
      : null) || "";

  const playSuccessSound = () => {
    try {
      const audio = new Audio(completionSoundUrl);
      audio.volume = 0.7;
      void audio.play();
    } catch (_error) {
      // Ignore audio errors (autoplay restrictions, unsupported API).
    }
  };

  const celebrateSuccess = () => {
    confettiService.triggerCelebrationConfetti();
    playSuccessSound();
  };

  const getExportFilename = () => {
    const rawName = (project.name || "360-tour").trim() || "360-tour";
    const safeName = rawName.replace(/[\\/:*?"<>|]/g, "-");
    const storageKey = `export-version:${project.id}`;
    const current = Number.parseInt(localStorage.getItem(storageKey) || "0", 10);
    const next = Number.isFinite(current) ? current + 1 : 1;
    localStorage.setItem(storageKey, String(next));
    return `${safeName}_v${next}.zip`;
  };

  // Load storage provider on mount
  useEffect(() => {
    const loadProvider = async () => {
      try {
        const p = await StorageServiceFactory.getCraftspaceStorageProvider(craftspaceId);
        setProvider(p);
      } catch (_e) {
        setProvider("360crafter");
      }
    };
    void loadProvider();
  }, [craftspaceId]);

  useEffect(() => {
    setPublishedUrl(null);
    setPublishedTourUrl(null);
    setPublishedProductionUrl(null);
  }, [project.id]);

  const getProjectSlug = () => {
    const data = project.data as Record<string, unknown> | null;
    if (data && typeof data.customSlug === "string" && data.customSlug.trim()) {
      return data.customSlug.trim();
    }
    return project.name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
  };

  const joinUrl = (base: string, path: string) => {
    if (!base) return path;
    const trimmedBase = base.endsWith("/") ? base.slice(0, -1) : base;
    const trimmedPath = path.startsWith("/") ? path.slice(1) : path;
    return `${trimmedBase}/${trimmedPath}`;
  };

  const craftspaceSlug =
    typeof craftspace?.slug === "string" && craftspace.slug.trim().length > 0
      ? craftspace.slug
      : "published";
  const projectSlug = getProjectSlug();
  const pullZoneUrl =
    (typeof import.meta.env.VITE_BUNNY_PUBLIC_PULL_ZONE_URL === "string" &&
      import.meta.env.VITE_BUNNY_PUBLIC_PULL_ZONE_URL) ||
    (typeof import.meta.env.VITE_BUNNY_PULL_ZONE_URL === "string"
      ? import.meta.env.VITE_BUNNY_PULL_ZONE_URL
      : "");
  const defaultPublishUrl = pullZoneUrl
    ? joinUrl(pullZoneUrl, `${craftspaceSlug}/${projectSlug}/`)
    : basePublicUrl;
  const productionPullZoneUrl =
    (typeof import.meta.env.VITE_BUNNY_PRODUCTION_PULL_ZONE_URL === "string" &&
      import.meta.env.VITE_BUNNY_PRODUCTION_PULL_ZONE_URL) ||
    "";
  // Use the project's internal ID as an opaque, stable slug for the production URL
  const productionSlug = (projectInternalId || `${craftspaceSlug}${projectSlug}`).slice(0, 11);
  const productionPublishUrl = productionPullZoneUrl
    ? joinUrl(productionPullZoneUrl, `${productionSlug}/`)
    : null;
  const selectedDomains = (customDomains || []).filter((d) => d.is_selected);
  const primarySelectedDomain =
    selectedDomains.find((d) => d.status === "verified") ||
    selectedDomains[0] ||
    null;
  const computedPublishedUrl = publishedUrl || latestPublishedTour?.bucket_url || defaultPublishUrl;
  const normalizePublishUrl = (url: string) => url.trim().replace(/\/+$/, "");
  const isDefaultPublished =
    (!!publishedUrl &&
      normalizePublishUrl(publishedUrl) === normalizePublishUrl(defaultPublishUrl)) ||
    (!!publishedTourUrl &&
      normalizePublishUrl(publishedTourUrl) === normalizePublishUrl(defaultPublishUrl)) ||
    (!!latestPublishedTour?.bucket_url &&
      normalizePublishUrl(latestPublishedTour.bucket_url) === normalizePublishUrl(defaultPublishUrl)) ||
    (!!latestPublishedTour?.tour_url &&
      normalizePublishUrl(latestPublishedTour.tour_url) === normalizePublishUrl(defaultPublishUrl));
  const isProductionPublished =
    !!productionPublishUrl &&
    !!publishedProductionUrl &&
    normalizePublishUrl(publishedProductionUrl) === normalizePublishUrl(productionPublishUrl);
  const isDomainPublished = (domain: { domain: string }) => {
    if (!publishedTourUrl) return false;
    const host = domain.domain.replace(/^https?:\/\//, "").replace(/\/.*/, "");
    const domainUrl = `https://${host}/`;
    return normalizePublishUrl(publishedTourUrl) === normalizePublishUrl(domainUrl);
  };
  const verifiedSelectedDomains = selectedDomains.filter((domain) => domain.status === "verified");
  const needsVerification =
    selectedDomains.length > 0 && selectedDomains.some((domain) => domain.status !== "verified");

  useEffect(() => {
    if (selectedDomains.length === 0) return;
    const runAutoCheck = async () => {
      for (const domain of selectedDomains) {
        if (checkedDomainIdsRef.current.has(domain._id)) continue;
        checkedDomainIdsRef.current.add(domain._id);
        try {
          const result = (await verifyDomain({ id: domain._id as any })) as {
            verified: boolean;
            txtVerified?: boolean;
            cnameVerified?: boolean;
          };
          setDomainVerifyHints((prev) => ({
            ...prev,
            [domain._id]: {
              txt: result.txtVerified ?? null,
              cname: result.cnameVerified ?? null,
            },
          }));
        } catch (_error) {
          // Ignore auto-check errors; user can manually check.
        }
      }
    };
    void runAutoCheck();
  }, [selectedDomains, verifyDomain]);

  useEffect(() => {
    if (!customDomains || customDomains.length === 0) return;
    setExpandedDomainIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const domain of customDomains) {
        if (domain.status !== "verified" && !next.has(domain._id)) {
          next.add(domain._id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [customDomains]);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      const created = (await createDomain({
        projectId: projectInternalId as unknown as any,
        domain: newDomain,
      })) as { _id: string };
      setNewDomain("");
      setShowAddDomain(false);
      toast({ title: "Domain added", description: "Add the TXT + CNAME records to verify." });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Add failed",
        description: error instanceof Error ? error.message : "Failed to add domain",
      });
    }
  };

  const handleCheckStatus = async (id: string) => {
    try {
      setDomainActionId(id);
      const result = (await verifyDomain({ id: id as any })) as {
        verified: boolean;
        txtVerified?: boolean;
        cnameVerified?: boolean;
      };
      setDomainVerifyHints((prev) => ({
        ...prev,
        [id]: {
          txt: result.txtVerified ?? null,
          cname: result.cnameVerified ?? null,
        },
      }));
      if (!result.verified) {
        // No toast needed; the red X icons in the DNS records indicate status.
      } else {
        toast({ title: "Verified", description: "TXT and CNAME are set correctly." });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Verification failed",
        description: error instanceof Error ? error.message : "Could not verify domain",
      });
    } finally {
      setDomainActionId(null);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      if (!user && !allowLocalExport) {
        toast({
          variant: "destructive",
          title: "Sign in required",
          description: "Please sign in before exporting a tour.",
        });
        return;
      }

      if (allowLocalExport) {
        await exportWebTour(project, panoramas, hotspots, { download: true });
        toast({
          title: "Export successful",
          description: "Your 360° tour has been exported successfully.",
        });
        celebrateSuccess();
        onClose();
        return;
      }
      const primarySelectedId = primarySelectedDomain?._id;
      const projectForPublish =
        primarySelectedId &&
        (project as { custom_domain_id?: string }).custom_domain_id !== primarySelectedId
          ? ({ ...project, custom_domain_id: primarySelectedId } as Project)
          : project;
      const result = await publishWebTour(projectForPublish, panoramas, hotspots, user);

      if (result.exportBlob) {
        downloadFile(result.exportBlob, getExportFilename());
      }

      toast({
        title: "Export successful",
        description: "Your 360° tour has been exported successfully.",
      });
      celebrateSuccess();

      onClose();
    } catch (error) {
      console.error("Export failed:", error);
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "An error occurred while exporting. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handlePublishDirectly = async () => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Sign in required",
        description: "Please sign in before publishing a tour.",
      });
      return;
    }

    const primarySelectedId = primarySelectedDomain?._id;
    const projectForPublish =
      primarySelectedId &&
      (project as { custom_domain_id?: string }).custom_domain_id !== primarySelectedId
        ? ({ ...project, custom_domain_id: primarySelectedId } as Project)
        : project;

    setIsPublishing(true);
    try {
      if (selectedDomains.length > 0) {
        const verifiedIds = new Set<string>();
        for (const domain of selectedDomains) {
          const verifyResult = (await verifyDomain({ id: domain._id as any })) as {
            verified: boolean;
            txtVerified?: boolean;
            cnameVerified?: boolean;
          };
          setDomainVerifyHints((prev) => ({
            ...prev,
            [domain._id]: {
              txt: verifyResult.txtVerified ?? null,
              cname: verifyResult.cnameVerified ?? null,
            },
          }));
          if (!verifyResult.verified) {
            setHighlightTrash(true);
            setTimeout(() => setHighlightTrash(false), 1200);
            return;
          }
          verifiedIds.add(domain._id);
        }

        for (const domain of selectedDomains) {
          if (!verifiedIds.has(domain._id)) continue;
          const sslResult = (await ensureSsl({
            hostname: domain.domain,
          })) as { hasCertificate: boolean; autoSslEnabled: boolean };
          if (!sslResult.hasCertificate) {
            toast({
              title: "SSL is being provisioned",
              description: "It can take a few minutes for HTTPS to become active.",
            });
          }
          await ensureEdgeRule({
            domainId: domain._id as any,
            projectId: projectInternalId as any,
          });
        }
      }

      const result = await publishWebTour(
        projectForPublish,
        panoramas,
        hotspots,
        user,
        undefined,
        undefined,
        productionChecked && productionSlug ? { productionSlug } : undefined,
      );
      setPublishedUrl(result.bucketUrl);
      setPublishedTourUrl(result.url);
      if (productionChecked && productionPublishUrl) {
        setPublishedProductionUrl(productionPublishUrl);
      }
      toast({
        title: "Publish successful!",
        description: "Your 360° tour has been published to S3.",
      });
      celebrateSuccess();
    } catch (error) {
      console.error("Publish failed:", error);
      toast({
        variant: "destructive",
        title: "Publish failed",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while publishing. Please try again.",
      });
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <>
      {/* Click-outside backdrop */}
      <div className="fixed inset-0 z-[4990]" onClick={onClose} />

      {/* Floating panel */}
      <div className="fixed top-[52px] right-4 z-[5000] w-[480px] max-h-[calc(100vh-70px)] bg-gray-800 border border-gray-700 rounded-lg shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2 text-white">
            <span className="font-semibold text-sm">Choose publish destination</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white w-6 h-6 flex items-center justify-center rounded hover:bg-gray-700 transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 p-4 space-y-5">
          <section className="space-y-3">

            <div className="space-y-4">
              {/* Staging */}
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Staging</p>
                <div className="rounded-md border border-gray-700 px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={stagingChecked}
                      onChange={(e) => setStagingChecked(e.target.checked)}
                      className="h-4 w-3 rounded border-gray-600 bg-gray-700 text-blue-500 accent-blue-500 flex-shrink-0"
                    />
                    <span className="text-gray-200 leading-none truncate">{defaultPublishUrl}</span>
                    {isDefaultPublished && (
                      <a
                        href={computedPublishedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
                        title="Open in new tab"
                      >
                        <ArrowUpRight size={14} />
                      </a>
                    )}
                  </div>
                  <p className="mt-1 ml-6 text-xs text-gray-500">
                    {isDefaultPublished ? "Published" : "Not published"}
                  </p>
                </div>
              </div>

              {/* Production */}
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Production</p>
                <div className="space-y-2">
              {productionPublishUrl && (
                <div className="rounded-md border border-gray-700 px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={productionChecked}
                      onChange={(e) => setProductionChecked(e.target.checked)}
                      className="h-4 w-3 rounded border-gray-600 bg-gray-700 text-blue-500 accent-blue-500 flex-shrink-0"
                    />
                    <span className="text-gray-200 leading-none truncate">{productionPublishUrl}</span>
                    {isProductionPublished && (
                      <a
                        href={productionPublishUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 transition-colors"
                        title="Open in new tab"
                      >
                        <ArrowUpRight size={14} />
                      </a>
                    )}
                  </div>
                  <p className="mt-1 ml-6 text-xs text-gray-500">
                    {isProductionPublished ? "Published" : "Not published"}
                  </p>
                </div>
              )}
              {(customDomains || []).map((domain) => {
                const isExpanded = expandedDomainIds.has(domain._id);
                const showChevron = hoveredDomainId === domain._id || isExpanded;
                return (
                  <div
                    key={domain._id}
                    className="rounded-md border border-gray-700 px-3 py-2 text-sm"
                    onMouseEnter={() => setHoveredDomainId(domain._id)}
                    onMouseLeave={() => setHoveredDomainId(null)}
                  >
                    <div
                      className="flex items-center justify-between"
                      onClick={() => {
                        setExpandedDomainIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(domain._id)) {
                            next.delete(domain._id);
                          } else {
                            next.add(domain._id);
                          }
                          return next;
                        });
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedDomainIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(domain._id)) {
                                next.delete(domain._id);
                              } else {
                                next.add(domain._id);
                              }
                              return next;
                            });
                          }}
                          className={`flex h-5 w-5 items-center justify-center rounded text-gray-400 hover:text-gray-200 ${
                            showChevron ? "opacity-100" : "opacity-0"
                          }`}
                          aria-label={isExpanded ? "Collapse DNS records" : "Expand DNS records"}
                        >
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        <div className="flex items-center gap-1 h-5">
                          <input
                            type="checkbox"
                            checked={Boolean(domain.is_selected)}
                            onChange={async (e) => {
                              e.stopPropagation();
                              await setSelected({
                                id: domain._id as any,
                                selected: e.target.checked,
                              });
                            }}
                            className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-500 accent-blue-500"
                          />
                          {isDomainPublished(domain) ? (
                            <CheckCircle2 className="h-4 w-4 text-green-400" />
                          ) : domain.status === "verified" ? (
                            <div className="h-4 w-4 rounded-full bg-blue-400" />
                          ) : (
                            <div className="h-4 w-4 rounded-full bg-yellow-400" />
                          )}
                        </div>
                        <span className="text-left text-gray-200 cursor-pointer leading-none">
                          {domain.domain}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" />
                    </div>
                    {isExpanded && (
                      <div className="mt-3 border-t border-gray-700 pt-3 text-xs text-gray-300 space-y-2">
                        <div className="flex items-center">
                          <Button
                            size="sm"
                            className="bg-gray-700 hover:bg-gray-600 text-white w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleCheckStatus(domain._id);
                            }}
                            disabled={domainActionId === domain._id}
                          >
                            {domainActionId === domain._id ? "Checking..." : "Check status"}
                          </Button>
                        </div>
                        <div className="flex items-center">
                          <Button
                            size="sm"
                            className={`bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-600 w-full ${
                              highlightTrash && domain.status !== "verified"
                                ? "ring-2 ring-orange-400 animate-pulse"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void removeDomain({ id: domain._id as any });
                            }}
                          >
                            Remove domain
                          </Button>
                        </div>
                        {(() => {
                          const name = `_360crafter-verify.${domain.domain}`;
                          const value = domain.token;
                          const bunnyHost =
                            (typeof import.meta.env.VITE_BUNNY_PRODUCTION_PULL_ZONE_HOST === "string" &&
                              import.meta.env.VITE_BUNNY_PRODUCTION_PULL_ZONE_HOST) ||
                            (typeof import.meta.env.VITE_BUNNY_PULL_ZONE_HOST === "string" &&
                              import.meta.env.VITE_BUNNY_PULL_ZONE_HOST) ||
                            (() => {
                              const bunnyPullUrl =
                                (typeof import.meta.env.VITE_BUNNY_PULL_ZONE_URL === "string" &&
                                  import.meta.env.VITE_BUNNY_PULL_ZONE_URL) ||
                                "";
                              return bunnyPullUrl
                                .replace("https://", "")
                                .replace("http://", "")
                                .replace(/\/.*/, "");
                            })();
                          return (
                            <div className="space-y-2">
                              <div className="grid grid-cols-3 gap-2 text-[11px] text-gray-500">
                                <div>TYPE</div>
                                <div>NAME</div>
                                <div>VALUE</div>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-[11px] items-center">
                                <div className="text-gray-200 flex items-center gap-1">
                                  <span>TXT</span>
                                  {(() => {
                                    const hint = domainVerifyHints[domain._id]?.txt;
                                    if (hint === true) {
                                      return <CheckCircle2 size={12} className="text-green-400" />;
                                    }
                                    if (hint === false) {
                                      return <X size={12} className="text-red-400" />;
                                    }
                                    return <span className="h-2 w-2 rounded-full bg-gray-500" />;
                                  })()}
                                </div>
                                <button
                                  type="button"
                                  className="text-left text-gray-200 break-all whitespace-normal underline-offset-2 hover:underline"
                                  onMouseEnter={(e) => {
                                    const { clientX, clientY } = e;
                                    setCopyTooltip({
                                      visible: true,
                                      x: clientX,
                                      y: clientY,
                                      text: "Click to copy URL",
                                    });
                                  }}
                                  onMouseMove={(e) => {
                                    const { clientX, clientY } = e;
                                    setCopyTooltip((prev) => ({
                                      ...prev,
                                      visible: true,
                                      x: clientX,
                                      y: clientY,
                                    }));
                                  }}
                                  onMouseLeave={() =>
                                    setCopyTooltip({
                                      visible: false,
                                      x: 0,
                                      y: 0,
                                      text: "Click to copy URL",
                                    })
                                  }
                                  onClick={async () => {
                                    await navigator.clipboard.writeText(name);
                                    setCopyTooltip((prev) => ({
                                      ...prev,
                                      text: "Copied to clipboard",
                                    }));
                                  }}
                                >
                                  {name}
                                </button>
                                <button
                                  type="button"
                                  className="text-left text-gray-200 break-all whitespace-normal underline-offset-2 hover:underline"
                                  onMouseEnter={(e) => {
                                    const { clientX, clientY } = e;
                                    setCopyTooltip({
                                      visible: true,
                                      x: clientX,
                                      y: clientY,
                                      text: "Click to copy URL",
                                    });
                                  }}
                                  onMouseMove={(e) => {
                                    const { clientX, clientY } = e;
                                    setCopyTooltip((prev) => ({
                                      ...prev,
                                      visible: true,
                                      x: clientX,
                                      y: clientY,
                                    }));
                                  }}
                                  onMouseLeave={() =>
                                    setCopyTooltip({
                                      visible: false,
                                      x: 0,
                                      y: 0,
                                      text: "Click to copy URL",
                                    })
                                  }
                                  onClick={async () => {
                                    await navigator.clipboard.writeText(value);
                                    setCopyTooltip((prev) => ({
                                      ...prev,
                                      text: "Copied to clipboard",
                                    }));
                                  }}
                                >
                                  {value}
                                </button>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-[11px] items-center">
                                <div className="text-gray-200 flex items-center gap-1">
                                  <span>CNAME</span>
                                  {(() => {
                                    const hint = domainVerifyHints[domain._id]?.cname;
                                    if (hint === true) {
                                      return <CheckCircle2 size={12} className="text-green-400" />;
                                    }
                                    if (hint === false) {
                                      return <X size={12} className="text-red-400" />;
                                    }
                                    return <span className="h-2 w-2 rounded-full bg-gray-500" />;
                                  })()}
                                </div>
                                <button
                                  type="button"
                                  className="text-left text-gray-200 break-all whitespace-normal underline-offset-2 hover:underline"
                                  onMouseEnter={(e) => {
                                    const { clientX, clientY } = e;
                                    setCopyTooltip({
                                      visible: true,
                                      x: clientX,
                                      y: clientY,
                                      text: "Click to copy URL",
                                    });
                                  }}
                                  onMouseMove={(e) => {
                                    const { clientX, clientY } = e;
                                    setCopyTooltip((prev) => ({
                                      ...prev,
                                      visible: true,
                                      x: clientX,
                                      y: clientY,
                                    }));
                                  }}
                                  onMouseLeave={() =>
                                    setCopyTooltip({
                                      visible: false,
                                      x: 0,
                                      y: 0,
                                      text: "Click to copy URL",
                                    })
                                  }
                                  onClick={async () => {
                                    await navigator.clipboard.writeText(domain.domain);
                                    setCopyTooltip((prev) => ({
                                      ...prev,
                                      text: "Copied to clipboard",
                                    }));
                                  }}
                                >
                                  {domain.domain}
                                </button>
                                <button
                                  type="button"
                                  className="text-left text-gray-200 break-all whitespace-normal underline-offset-2 hover:underline"
                                  onMouseEnter={(e) => {
                                    const { clientX, clientY } = e;
                                    setCopyTooltip({
                                      visible: true,
                                      x: clientX,
                                      y: clientY,
                                      text: "Click to copy URL",
                                    });
                                  }}
                                  onMouseMove={(e) => {
                                    const { clientX, clientY } = e;
                                    setCopyTooltip((prev) => ({
                                      ...prev,
                                      visible: true,
                                      x: clientX,
                                      y: clientY,
                                    }));
                                  }}
                                  onMouseLeave={() =>
                                    setCopyTooltip({
                                      visible: false,
                                      x: 0,
                                      y: 0,
                                      text: "Click to copy URL",
                                    })
                                  }
                                  onClick={async () => {
                                    await navigator.clipboard.writeText((bunnyHost || "").replace(/^https?:\/\//, ""));
                                    setCopyTooltip((prev) => ({
                                      ...prev,
                                      text: "Copied to clipboard",
                                    }));
                                  }}
                                >
                                  {(bunnyHost || "your-pull-zone.b-cdn.net").replace(/^https?:\/\//, "")}
                                </button>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
              {selectedDomains.length > 0 && (
                <button
                  type="button"
                  onClick={async () => {
                    for (const domain of selectedDomains) {
                      await setSelected({ id: domain._id as any, selected: false });
                    }
                    await clearDomain({ projectId: projectInternalId as unknown as any });
                  }}
                  className="hidden"
                  aria-hidden="true"
                  tabIndex={-1}
                />
              )}

                  {/* Add custom domain inline form or link */}
                  {showAddDomain ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        placeholder="your-domain.com"
                        className="w-full bg-gray-800 border border-gray-600 text-gray-300 px-2 py-1.5 rounded text-xs focus:outline-none focus:ring-0 focus:border-gray-600"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === "Enter") void handleAddDomain(); }}
                      />
                      <Button
                        onClick={handleAddDomain}
                        className="bg-blue-600 hover:bg-blue-700 w-full"
                        size="sm"
                      >
                        Add domain
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowAddDomain(true)}
                      className="text-xs text-blue-400 hover:text-blue-300 hover:underline"
                    >
                      Add a custom domain
                    </button>
                  )}
                </div>{/* end space-y-2 custom domains */}
              </div>{/* end Production */}
            </div>{/* end space-y-4 */}

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={isExporting}
                className="border-gray-600 text-gray-300 hover:bg-gray-800 flex-shrink-0"
                size="sm"
                title="Download export"
              >
                <Download size={14} /> Export
              </Button>
              <div
                className="relative"
                onMouseEnter={(e) => {
                  if (!needsVerification) return;
                  setPublishTooltip({ visible: true, x: e.clientX, y: e.clientY });
                }}
                onMouseMove={(e) => {
                  if (!needsVerification) return;
                  setPublishTooltip({ visible: true, x: e.clientX, y: e.clientY });
                }}
                onMouseLeave={() => setPublishTooltip({ visible: false, x: 0, y: 0 })}
              >
                {publishTooltip.visible && (
                  <div
                    className="pointer-events-none fixed z-[5100] rounded bg-gray-900/95 border border-gray-700 px-2 py-1 text-[11px] text-gray-200 shadow"
                    style={{ left: publishTooltip.x + 12, top: publishTooltip.y + 12 }}
                  >
                    Wait a few minutes for DNS records or remove custom domain.
                  </div>
                )}
                <Button
                  onClick={handlePublishDirectly}
                  disabled={isPublishing || (!stagingChecked && !productionChecked && selectedDomains.length === 0)}
                  className="text-white whitespace-nowrap"
                  style={{ backgroundColor: "#1A7F37" }}
                  size="sm"
                >
                  {isPublishing ? "Publishing..." : needsVerification ? "Check status" : "Publish to selected domains"}
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
      {copyTooltip.visible && (
        <div
          className="fixed z-[9999] pointer-events-none px-2 py-1 rounded bg-gray-800 text-white text-[11px] shadow-lg border border-gray-600"
          style={{ left: copyTooltip.x + 12, top: copyTooltip.y - 28 }}
        >
          {copyTooltip.text}
        </div>
      )}
    </>
  );
};
